import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Star, CheckCircle, MessageSquare, Calendar, User, Mail } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { insertGuestReviewSchema } from "@shared/schema";
import { z } from "zod";
import { FormWizard, type WizardStep } from "@/components/ui/form-wizard";

const reviewFormSchema = insertGuestReviewSchema.extend({
  review_text: z.string().max(500, "Review must be 500 characters or less").optional(),
});

type ReviewFormData = z.infer<typeof reviewFormSchema>;

interface ReviewSubmissionFormProps {
  trigger?: React.ReactNode;
  /** Render as page content instead of dialog */
  standalone?: boolean;
  /** Pre-fill from email link query params */
  prefill?: {
    name?: string;
    email?: string;
    checkin?: string;
    checkout?: string;
  };
  /** Skip verification when arriving from tokenized email link */
  startAtStep2?: boolean;
}

export function ReviewSubmissionForm({ trigger, standalone = false, prefill, startAtStep2 = false }: ReviewSubmissionFormProps) {
  const [open, setOpen] = useState(standalone);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [verificationResult, setVerificationResult] = useState<{eligible: boolean; verified: boolean} | null>(null);
  const { toast } = useToast();

  const form = useForm<ReviewFormData>({
    resolver: zodResolver(reviewFormSchema),
    defaultValues: {
      guest_name: "",
      guest_email: "",
      location: "",
      rating: 0,
      stay_start_date: "",
      stay_end_date: "",
      trip_type: undefined,
      review_text: "",
      would_recommend: true,
    },
  });

  const watchedText = form.watch("review_text");
  const characterCount = watchedText?.length || 0;

  useEffect(() => {
    if (!prefill) return;
    if (prefill.name) form.setValue("guest_name", prefill.name);
    if (prefill.email) form.setValue("guest_email", prefill.email);
    if (prefill.checkin) form.setValue("stay_start_date", prefill.checkin.split("T")[0]);
    if (prefill.checkout) form.setValue("stay_end_date", prefill.checkout.split("T")[0]);
    if (startAtStep2) {
      setVerificationResult({ eligible: true, verified: true });
    }
  }, [prefill, startAtStep2, form]);

  // Verification mutation
  const verifyEligibility = useMutation({
    mutationFn: async (data: { email: string; start_date: string; end_date: string }) => {
      const params = new URLSearchParams({
        email: data.email,
        start_date: data.start_date,
        end_date: data.end_date,
      });
      const response = await fetch(`/api/reviews/verify?${params}`);
      if (!response.ok) throw new Error("Failed to verify eligibility");
      return response.json();
    },
    onSuccess: (data) => {
      setVerificationResult(data);
      if (data.eligible) {
        toast({
          title: "Verification successful!",
          description: data.verified
            ? "We found your booking and verified your stay."
            : "We found a booking with your email. You can proceed with your review.",
        });
      } else {
        toast({
          title: "Unable to verify stay",
          description: "We couldn't find a booking matching your email and dates. Please check your information.",
          variant: "destructive",
        });
      }
    },
    onError: () => {
      toast({
        title: "Verification failed",
        description: "There was an error verifying your stay. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Review submission mutation
  const submitReview = useMutation({
    mutationFn: (data: ReviewFormData) => apiRequest("POST", "/api/reviews/submit", data),
    onSuccess: () => {
      toast({
        title: "Review submitted successfully!",
        description: "Thank you for your feedback! Your review will be published after approval.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/reviews/public"] });
      queryClient.invalidateQueries({ queryKey: ["/api/reviews/summary"] });
      setOpen(false);
      form.reset();
      setRating(0);
      setVerificationResult(null);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to submit review",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });

  // Runs the same /api/reviews/verify check as before, adapted to the wizard's
  // per-step validate contract: advance only when the stay is eligible.
  const validateVerification = async (): Promise<boolean> => {
    const email = form.getValues("guest_email");
    const startDate = form.getValues("stay_start_date");
    const endDate = form.getValues("stay_end_date");

    if (!email || !startDate || !endDate) {
      toast({
        title: "Missing information",
        description: "Please fill in your email and stay dates.",
        variant: "destructive",
      });
      return false;
    }

    try {
      const data = await verifyEligibility.mutateAsync({
        email,
        start_date: startDate,
        end_date: endDate,
      });
      return !!data.eligible;
    } catch {
      return false;
    }
  };

  const onSubmit = (data: ReviewFormData) => {
    const reviewData = {
      ...data,
      rating,
    };
    submitReview.mutate(reviewData);
  };

  const renderStars = (currentRating: number, interactive = false, size = "w-8 h-8") => {
    return Array.from({ length: 5 }, (_, i) => {
      const starValue = i + 1;
      const isActive = starValue <= (interactive ? (hoveredRating || rating) : currentRating);

      return (
        <Star
          key={i}
          className={`${size} cursor-${interactive ? 'pointer' : 'default'} transition-colors ${
            isActive ? 'text-sun-hover fill-current' : 'text-muted-foreground/50'
          }`}
          onClick={interactive ? () => {
            setRating(starValue);
            form.setValue("rating", starValue);
          } : undefined}
          onMouseEnter={interactive ? () => setHoveredRating(starValue) : undefined}
          onMouseLeave={interactive ? () => setHoveredRating(0) : undefined}
          data-testid={`rating-star-${starValue}`}
        />
      );
    });
  };

  const verifiedBanner = verificationResult ? (
    <div className="p-4 bg-sand/60 border border-sand-deep flex items-start gap-3">
      <CheckCircle className="w-5 h-5 text-palm mt-0.5" />
      <div>
        <p className="text-luxury-sm font-normal text-palm">Stay verified!</p>
        <p className="text-luxury-sm text-palm mt-1">
          {verificationResult.verified
            ? "We found your booking and verified your stay dates."
            : "We found a booking with your email."
          }
        </p>
        {verificationResult.verified && (
          <Badge variant="secondary" className="mt-2 bg-sand/60 text-palm">
            Verified Guest
          </Badge>
        )}
      </div>
    </div>
  ) : null;

  const aboutYouStep: WizardStep = {
    title: "About you",
    validate: () => form.trigger(["guest_name", "guest_email"]),
    content: (
      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="guest_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Your Name
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter your full name"
                    {...field}
                    data-testid="input-guest-name"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="guest_email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    {...field}
                    data-testid="input-guest-email"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Your Location (Optional)</FormLabel>
              <FormControl>
                <Input
                  placeholder="City, State/Country"
                  {...field}
                  value={field.value ?? ""}
                  data-testid="input-location"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    ),
  };

  const yourStayStep: WizardStep = {
    title: "Your stay",
    validate: async () => {
      const ok = await form.trigger(["stay_start_date", "stay_end_date"]);
      if (!ok) return false;
      return validateVerification();
    },
    content: (
      <div className="space-y-4">
        <p className="text-luxury-sm text-ink-soft">
          Let's verify that you stayed with us. This helps ensure authentic reviews.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="stay_start_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Check-in Date
                </FormLabel>
                <FormControl>
                  <Input
                    type="date"
                    {...field}
                    data-testid="input-checkin-date"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="stay_end_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Check-out Date
                </FormLabel>
                <FormControl>
                  <Input
                    type="date"
                    {...field}
                    data-testid="input-checkout-date"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    ),
  };

  const ratingStep: WizardStep = {
    title: "Your rating",
    validate: () => {
      if (rating === 0) {
        toast({
          title: "Please select a rating",
          description: "Tap a star to rate your stay.",
          variant: "destructive",
        });
        return false;
      }
      return true;
    },
    content: (
      <div className="space-y-6">
        {verifiedBanner}

        <div className="space-y-4">
          <FormLabel className="text-luxury-base font-serif font-normal">How would you rate your stay?</FormLabel>
          <div className="flex justify-center">
            <div className="flex space-x-2">
              {renderStars(rating, true)}
            </div>
          </div>
          {rating > 0 && (
            <p className="text-center text-luxury-sm text-muted-foreground">
              {rating === 1 && "Poor"}
              {rating === 2 && "Fair"}
              {rating === 3 && "Good"}
              {rating === 4 && "Very Good"}
              {rating === 5 && "Excellent"}
            </p>
          )}
        </div>
      </div>
    ),
  };

  const reviewStep: WizardStep = {
    title: "Your review",
    content: (
      <div className="space-y-6">
        <FormField
          control={form.control}
          name="trip_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Trip Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value ?? undefined}>
                <FormControl>
                  <SelectTrigger data-testid="select-trip-type">
                    <SelectValue placeholder="Select your trip type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Solo">Solo</SelectItem>
                  <SelectItem value="Couple">Couple</SelectItem>
                  <SelectItem value="Family">Family</SelectItem>
                  <SelectItem value="Group">Group</SelectItem>
                  <SelectItem value="Business">Business</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="review_text"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tell us about your experience (Optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Share details about your stay, what you loved, or suggestions for improvement..."
                  className="min-h-[120px] resize-none"
                  maxLength={500}
                  {...field}
                  data-testid="textarea-review-text"
                />
              </FormControl>
              <div className="flex justify-between items-center">
                <FormMessage />
                <span className={`text-luxury-sm ${characterCount > 450 ? 'text-orange-500' : 'text-muted-foreground'}`}>
                  {characterCount}/500
                </span>
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="would_recommend"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Would you recommend this place to others?</FormLabel>
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant={field.value === true ? "default" : "outline"}
                  onClick={() => field.onChange(true)}
                  className="flex-1"
                  data-testid="recommend-yes"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Yes, I'd recommend it
                </Button>
                <Button
                  type="button"
                  variant={field.value === false ? "default" : "outline"}
                  onClick={() => field.onChange(false)}
                  className="flex-1"
                  data-testid="recommend-no"
                >
                  No, I wouldn't recommend it
                </Button>
              </div>
            </FormItem>
          )}
        />
      </div>
    ),
  };

  const steps: WizardStep[] = startAtStep2
    ? [ratingStep, reviewStep]
    : [aboutYouStep, yourStayStep, ratingStep, reviewStep];

  const formBody = (
    <Form {...form}>
      <FormWizard
        steps={steps}
        onComplete={form.handleSubmit(onSubmit)}
        submitLabel="Submit review"
        submittingLabel="Submitting..."
        isSubmitting={submitReview.isPending}
      />
    </Form>
  );

  if (standalone) {
    return (
      <Card className="max-w-2xl mx-auto shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-luxury-2xl font-serif">
            <MessageSquare className="w-6 h-6 text-accent" />
            Share Your Experience
          </CardTitle>
        </CardHeader>
        <CardContent>{formBody}</CardContent>
      </Card>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            className="btn-bronze text-white font-normal py-3 px-8 rounded-lg"
            data-testid="submit-review-trigger"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            Write a Review
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-accent" />
            Share Your Experience
          </DialogTitle>
        </DialogHeader>
        {formBody}
      </DialogContent>
    </Dialog>
  );
}
